import { useEffect, useState, useMemo } from "react";
import api, { formatINR, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Eye } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import DataTable from "@/components/DataTable";

const emptyItem = () => ({ product_id: "", name: "", hsn_code: "", quantity: 1, unit_price: 0, gst_percent: 5 });

export default function PurchaseReturns() {
  const { user, activeBranchId } = useAuth();
  const [parties, setParties] = useState([]);
  const [products, setProducts] = useState([]);
  const [purchaseBills, setPurchaseBills] = useState([]);
  const [open, setOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const reload = () => setReloadKey((k) => k + 1);

  const isSuper = user?.role === "super_admin";

  const empty = useMemo(() => ({
    branch_id: activeBranchId || "",
    party_id: "",
    purchase_bill_id: "",
    return_date: new Date().toISOString().slice(0, 10),
    debit_note_number: "",
    reason: "",
    items: [emptyItem()],
    refund_method: "Party Credit",
    notes: "",
  }), [activeBranchId]);

  const [form, setForm] = useState(empty);

  // Load supporting data once (parties + products + purchase bills for the create dialog)
  useEffect(() => {
    (async () => {
      try {
        const [p, pb, pr] = await Promise.all([
          api.get("/parties"),
          api.get("/purchase-bills"),
          api.get("/products"),
        ]);
        setParties(Array.isArray(p.data) ? p.data : (p.data.rows || []));
        setPurchaseBills(Array.isArray(pb.data) ? pb.data : (pb.data.rows || []));
        setProducts(Array.isArray(pr.data) ? pr.data : (pr.data.rows || []));
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
  }, []);

  // When a purchase bill is selected, prefill items from it.
  const onPickBill = (billId) => {
    setForm((f) => ({ ...f, purchase_bill_id: billId }));
    if (!billId) return;
    const bill = purchaseBills.find((b) => b.id === billId);
    if (!bill) return;
    setForm((f) => ({
      ...f,
      branch_id: bill.branch_id || f.branch_id,
      party_id: bill.party_id || f.party_id,
      items: (bill.items || []).map((it, idx) => ({
        purchase_bill_item_index: idx,
        product_id: it.product_id || "",
        name: it.name,
        hsn_code: it.hsn_code || "",
        quantity: 1, // user must set actual qty being returned
        unit_price: it.unit_price || 0,
        gst_percent: it.gst_percent || 5,
      })),
    }));
  };

  const addRow = () => setForm({ ...form, items: [...form.items, emptyItem()] });
  const updateRow = (i, patch) => setForm({ ...form, items: form.items.map((it, idx) => idx === i ? { ...it, ...patch } : it) });
  const removeRow = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });

  const totals = useMemo(() => {
    const subtotal = form.items.reduce((s, it) => s + (Number(it.unit_price) || 0) * (Number(it.quantity) || 0), 0);
    const cgst = form.items.reduce((s, it) => s + (Number(it.unit_price) || 0) * (Number(it.quantity) || 0) * ((Number(it.gst_percent) || 0) / 200), 0);
    const grand = subtotal + cgst * 2;
    return { subtotal: subtotal.toFixed(2), cgst: cgst.toFixed(2), grand: grand.toFixed(2) };
  }, [form.items]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.party_id) { toast.error("Select a party"); return; }
    if (form.items.length === 0 || form.items.some((it) => !it.name || it.quantity <= 0)) {
      toast.error("Add at least one item with quantity > 0"); return;
    }
    try {
      await api.post("/purchase-returns", form);
      toast.success("Purchase return posted");
      setOpen(false); setForm(empty); reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onDelete = async (id) => {
    if (!confirm("Delete this purchase return? Stock + party-payment will be reversed.")) return;
    try { await api.delete(`/purchase-returns/${id}`); toast.success("Reversed"); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const view = async (id) => {
    try { const { data } = await api.get(`/purchase-returns/${id}`); setViewing(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const filteredParties = parties.filter((p) => !activeBranchId || p.branch_id === activeBranchId);
  const filteredBills = purchaseBills.filter((b) => (!activeBranchId || b.branch_id === activeBranchId) && (!form.party_id || b.party_id === form.party_id));

  return (
    <div className="space-y-6" data-testid="purchase-returns-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Purchase Returns</h1>
          <p className="text-sm text-slate-500">Debit notes against suppliers. Reduces party payable and inventory stock.</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setForm(empty); }}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800 rounded-none" data-testid="pr-add-btn"><Plus className="w-4 h-4 mr-2" />New Purchase Return</Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm max-w-5xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>New Purchase Return (Dr. Note)</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-3">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <Label>Party *</Label>
                    <Select value={form.party_id} onValueChange={(v) => setForm({ ...form, party_id: v })}>
                      <SelectTrigger className="rounded-none mt-1" data-testid="pr-party"><SelectValue placeholder="Select party" /></SelectTrigger>
                      <SelectContent>
                        {filteredParties.map((p) => <SelectItem key={p.id} value={p.id}>{p.party_name || p.trade_name || p.legal_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Against Purchase Bill (optional)</Label>
                    <Select value={form.purchase_bill_id || "_none"} onValueChange={(v) => onPickBill(v === "_none" ? "" : v)}>
                      <SelectTrigger className="rounded-none mt-1" data-testid="pr-bill"><SelectValue placeholder="(standalone)" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none">(Standalone — no source bill)</SelectItem>
                        {filteredBills.map((b) => <SelectItem key={b.id} value={b.id}>{b.bill_number || b.bill_number_sys} · ₹{b.grand_total}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Return Date *</Label><Input type="date" required value={form.return_date} onChange={(e) => setForm({ ...form, return_date: e.target.value })} className="rounded-none mt-1" data-testid="pr-date" /></div>
                  <div><Label>Dr. Note Number (optional)</Label><Input value={form.debit_note_number} onChange={(e) => setForm({ ...form, debit_note_number: e.target.value })} placeholder="Auto if blank" className="rounded-none mt-1" data-testid="pr-dr-no" /></div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <div>
                    <Label>Refund Method *</Label>
                    <Select value={form.refund_method} onValueChange={(v) => setForm({ ...form, refund_method: v })}>
                      <SelectTrigger className="rounded-none mt-1" data-testid="pr-refund-method"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Party Credit">Party Credit (reduce payable)</SelectItem>
                        <SelectItem value="Cash Refund">Cash Refund (from party)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2"><Label>Reason</Label><Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Damaged / wrong design / quality issue / etc." className="rounded-none mt-1" data-testid="pr-reason" /></div>
                </div>

                <div className="border border-slate-200">
                  <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-50 text-xs font-medium text-slate-600">
                    <div className="col-span-4">Item Name</div>
                    <div className="col-span-2">HSN</div>
                    <div className="col-span-2">Qty *</div>
                    <div className="col-span-2">Unit Price *</div>
                    <div className="col-span-1">GST %</div>
                    <div className="col-span-1"></div>
                  </div>
                  {form.items.map((it, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 px-3 py-2 border-t border-slate-100">
                      <div className="col-span-4">
                        <Select value={it.product_id || "_manual"} onValueChange={(v) => {
                          if (v === "_manual") { updateRow(i, { product_id: "" }); return; }
                          const p = products.find((x) => x.id === v);
                          if (p) updateRow(i, { product_id: p.id, name: p.name, hsn_code: p.hsn_code || "", unit_price: p.cost_price || 0, gst_percent: p.gst_percent || 5 });
                        }}>
                          <SelectTrigger className="rounded-none" data-testid={`pr-prod-${i}`}><SelectValue placeholder={it.name || "Pick product"} /></SelectTrigger>
                          <SelectContent className="max-h-72">
                            <SelectItem value="_manual">(manual entry)</SelectItem>
                            {products.slice(0, 500).map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · stock {p.stock}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        {!it.product_id && (
                          <Input value={it.name} onChange={(e) => updateRow(i, { name: e.target.value })} placeholder="Name (manual)" className="rounded-none mt-1" data-testid={`pr-name-${i}`} />
                        )}
                      </div>
                      <div className="col-span-2"><Input value={it.hsn_code} onChange={(e) => updateRow(i, { hsn_code: e.target.value })} className="rounded-none" data-testid={`pr-hsn-${i}`} /></div>
                      <div className="col-span-2"><Input type="number" min="0.01" step="any" value={it.quantity} onChange={(e) => updateRow(i, { quantity: parseFloat(e.target.value) || 0 })} className="rounded-none" data-testid={`pr-qty-${i}`} /></div>
                      <div className="col-span-2"><Input type="number" min="0" step="any" value={it.unit_price} onChange={(e) => updateRow(i, { unit_price: parseFloat(e.target.value) || 0 })} className="rounded-none" data-testid={`pr-price-${i}`} /></div>
                      <div className="col-span-1"><Input type="number" min="0" max="50" step="any" value={it.gst_percent} onChange={(e) => updateRow(i, { gst_percent: parseFloat(e.target.value) || 0 })} className="rounded-none" /></div>
                      <div className="col-span-1 flex items-center justify-end"><Button type="button" variant="ghost" size="sm" onClick={() => removeRow(i)} disabled={form.items.length === 1} data-testid={`pr-remove-${i}`}><Trash2 className="w-4 h-4" /></Button></div>
                    </div>
                  ))}
                  <div className="px-3 py-2 border-t border-slate-100">
                    <Button type="button" variant="outline" size="sm" onClick={addRow} className="rounded-none" data-testid="pr-add-row"><Plus className="w-3 h-3 mr-1" />Add Row</Button>
                  </div>
                </div>

                <div>
                  <Label>Notes</Label>
                  <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-none mt-1" rows={2} data-testid="pr-notes" />
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm bg-slate-50 p-3 border border-slate-200">
                  <div><div className="text-xs text-slate-500">Subtotal</div><div className="font-semibold">{formatINR(totals.subtotal)}</div></div>
                  <div><div className="text-xs text-slate-500">CGST</div><div className="font-semibold">{formatINR(totals.cgst)}</div></div>
                  <div><div className="text-xs text-slate-500">SGST</div><div className="font-semibold">{formatINR(totals.cgst)}</div></div>
                  <div><div className="text-xs text-slate-500">Grand Total</div><div className="font-semibold">{formatINR(totals.grand)}</div></div>
                </div>

                <DialogFooter><Button type="submit" className="bg-slate-900 hover:bg-slate-800 rounded-none" data-testid="pr-submit">Post Purchase Return</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <DataTable
        endpoint="/purchase-returns"
        columns={[
          { key: "debit_note_number", label: "Dr. Note", sortable: true, render: (r) => <span className="font-medium">{r.debit_note_number}</span> },
          { key: "return_date", label: "Date", sortable: true, render: (r) => r.return_date },
          { key: "party_name", label: "Party", render: (r) => r.party_name },
          { key: "purchase_bill_number", label: "Against Bill", render: (r) => <span className="text-slate-600">{r.purchase_bill_number || "—"}</span> },
          { key: "items", label: "Items", render: (r) => (r.items || []).length },
          { key: "grand_total", label: "Amount", sortable: true, className: "text-right", render: (r) => <span className="tabular-nums font-semibold">{formatINR(r.grand_total)}</span> },
          { key: "refund_method", label: "Refund Method", render: (r) => <span className="inline-block px-2 py-0.5 text-xs border border-slate-300 bg-slate-50">{r.refund_method}</span> },
          { key: "imported_from_tally", label: "Src", render: (r) => r.imported_from_tally ? <span className="text-[10px] uppercase text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5">Tally</span> : <span className="text-[10px] text-slate-400">Live</span> },
        ]}
        filters={[
          { key: "refund_method", type: "select", label: "Refund", options: [{ value: "Party Credit", label: "Party Credit" }, { value: "Cash Refund", label: "Cash Refund" }] },
          { key: "imported", type: "select", label: "Source", options: [{ value: "true", label: "Imported (Tally)" }, { value: "false", label: "Live only" }] },
          { key: "date_from", type: "date", label: "From date" },
          { key: "date_to", type: "date", label: "To date" },
        ]}
        searchPlaceholder="Search Dr.Note / Party / Bill / Reason..."
        extraQuery={{ branch_id: activeBranchId || undefined }}
        reloadKey={reloadKey}
        actions={(r) => (
          <div className="flex justify-end gap-1">
            <Button variant="ghost" size="sm" onClick={() => view(r.id)} data-testid={`pr-view-${r.id}`}><Eye className="w-4 h-4" /></Button>
            {isSuper && <Button variant="ghost" size="sm" onClick={() => onDelete(r.id)} data-testid={`pr-delete-${r.id}`}><Trash2 className="w-4 h-4 text-red-600" /></Button>}
          </div>
        )}
        dataTestid="pr-table"
      />

      <Dialog open={!!viewing} onOpenChange={(v) => !v && setViewing(null)}>
        <DialogContent className="rounded-sm max-w-3xl">
          <DialogHeader><DialogTitle>{viewing?.debit_note_number} · {viewing?.party_name}</DialogTitle></DialogHeader>
          {viewing && (
            <div className="text-sm space-y-3">
              <div className="grid grid-cols-3 gap-2">
                <div><span className="text-slate-500">Date:</span> {viewing.return_date}</div>
                <div><span className="text-slate-500">Method:</span> {viewing.refund_method}</div>
                <div><span className="text-slate-500">Bill:</span> {viewing.purchase_bill_number || "—"}</div>
              </div>
              <div><span className="text-slate-500">Reason:</span> {viewing.reason || "—"}</div>
              <table className="min-w-full text-xs border border-slate-200">
                <thead className="bg-slate-50">
                  <tr><th className="px-2 py-1 text-left">Item</th><th className="px-2 py-1">HSN</th><th className="px-2 py-1">Qty</th><th className="px-2 py-1">Rate</th><th className="px-2 py-1">GST%</th><th className="px-2 py-1">Total</th></tr>
                </thead>
                <tbody>
                  {(viewing.items || []).map((it, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="px-2 py-1">{it.name}</td>
                      <td className="px-2 py-1 text-center">{it.hsn_code || "—"}</td>
                      <td className="px-2 py-1 text-right">{it.quantity}</td>
                      <td className="px-2 py-1 text-right">{formatINR(it.unit_price)}</td>
                      <td className="px-2 py-1 text-right">{it.gst_percent}</td>
                      <td className="px-2 py-1 text-right">{formatINR(it.line_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 bg-slate-50 p-2 border border-slate-200">
                <div><div className="text-xs text-slate-500">Subtotal</div><div>{formatINR(viewing.subtotal)}</div></div>
                <div><div className="text-xs text-slate-500">CGST</div><div>{formatINR(viewing.cgst_total)}</div></div>
                <div><div className="text-xs text-slate-500">SGST</div><div>{formatINR(viewing.sgst_total)}</div></div>
                <div><div className="text-xs text-slate-500">Grand Total</div><div className="font-semibold">{formatINR(viewing.grand_total)}</div></div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
